import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {Button} from "primeng/button";
import {InputText} from "primeng/inputtext";
import {MessageService, PrimeTemplate} from "primeng/api";
import {FormBuilder, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TableModule} from "primeng/table";
import {Tag} from "primeng/tag";
import {UserOverview} from "../../../model/user.model";
import {UserService} from "../../../services/user.service";
import {DatePipe} from "@angular/common";
import {Select} from "primeng/select";
import {finalize, Observable} from "rxjs";
import {RouterLink} from "@angular/router";

const options = [
  {
    label: 'Signup Flow',
    value: 'signup-flow'
  },
  {
    label: 'Mandatory Flow',
    value: 'mandatory-flow'
  },
  {
    label: 'Active',
    value: 'active'
  },
  {
    label: 'To register in NYC',
    value: 'to-register'
  },
  {
    label: 'Not synced',
    value: 'not-synced'
  }
] as const;

@Component({
  selector: 'app-users-tab',
  standalone: true,
  imports: [
    Button,
    InputText,
    PrimeTemplate,
    ReactiveFormsModule,
    TableModule,
    Tag,
    DatePipe,
    Select,
    FormsModule,
    RouterLink
  ],
  templateUrl: './users-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsersTabComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  constructor(private readonly fb: FormBuilder, private readonly userService: UserService) {

  }

  ngOnInit(): void {
    this.userService.getActiveUsersNotRegistered().subscribe(newUsers => this.users.set(newUsers));
  }

  public readonly users = signal<UserOverview[]>([]);
  public readonly sortedUsers = computed(() => [...this.users()].sort(
      (a, b) => {
        if (a.ignore && !b.ignore) return 1;
        if (!a.ignore && b.ignore) return -1;
        if (a.createdAt > b.createdAt) return -1;
        if (a.createdAt < b.createdAt) return 1;
        return 0;
      }
    ));

  protected readonly selectedUser = signal<UserOverview | undefined>(undefined);
  options: {
    label: string,
    value: string
  }[] = [...options];
  protected readonly emailSearchLoading = signal(false);
  protected readonly userIdSearchLoading = signal(false);
  protected readonly statusSearchLoading = signal(false);
  protected email = "";
  protected userId = "";
  protected statusOption: typeof options[number]['value'] = 'to-register';

  onDeleteClick() {
    const confirm = window.confirm('Are you sure you want to delete this user?');

    if (confirm) {
      this.userService.deleteUser(this.selectedUser()!.id).subscribe(
        () => this.messageService.add(
          {
            severity: 'success',
            summary: 'User deleted',
            detail: `User ${this.selectedUser()!.email} deleted`
          }
        )
      );
    }
  }

  onEmailSearchClick() {
    this.emailSearchLoading.set(true);

    this.userService.getByEmail(this.email).pipe(finalize(() => this.emailSearchLoading.set(false))).subscribe(
      users => {
        this.users.set(users);
      }
    );
  }

  onUserIdSearchClick() {
    this.userIdSearchLoading.set(true);

    this.userService.getById(this.userId).pipe(finalize(() => this.userIdSearchLoading.set(false))).subscribe(
      user => {
        this.users.set([user]);
      }
    );
  }

  onStatusSearchClick() {
    this.statusSearchLoading.set(true);

    let observable: Observable<UserOverview[]>;

    if (this.statusOption === 'to-register') {
      observable = this.userService.getActiveUsersNotRegistered();
    } else if (this.statusOption === 'not-synced') {
      observable = this.userService.getActiveNotSyncedUsers();
    } else {
      observable = this.userService.getByStatus(this.statusOption);
    }

    return observable.pipe(finalize(() => this.statusSearchLoading.set(false))).subscribe(
      users => {
        this.users.set(users);
      }
    );
  }

  downloadCsv() {
    this.userService.requestCsvExport().subscribe({
      next: () => this.messageService.add({
        severity: 'success',
        summary: 'Export requested',
        detail: 'We will email you a download link shortly.'
      }),
      error: () => this.messageService.add({
        severity: 'error',
        summary: 'Export failed',
        detail: 'Please try again.'
      })
    });
  }

  getRegisterList() {
    this.userService.getAllNycAuth().subscribe(
      (data) => navigator.clipboard.writeText(JSON.stringify(data)).then(
        () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Copied to clipboard',
          });
        }
      ));
  }

  getFailedChildren() {
    this.userService.getFailedWithChildren().subscribe(
      (data) => navigator.clipboard.writeText(JSON.stringify(data)).then(
        () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Copied to clipboard',
          });
        }
      ));
  }
}
